import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import classnames from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom-v5-compat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles as faSparklesSolid } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { faArrowRotateRight } from '@fortawesome/pro-regular-svg-icons/faArrowRotateRight';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { Button as MantineButton } from '@mantine/core';

import { Button } from 'ui-components/components/button';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { FlexRow } from 'ui-components/components/flex/row';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { emptyArray } from 'ui-components/default-props/empty';

import { PackingErrorCode } from '../../../../../../../packing/services/packing-service';
import type { JobResultResponse, PackingError } from '../../../../../../../packing/services/packing-service';
import { getPackagesCustomSettingsPath } from '../../../../../../../routes/constants';

import type { MessageDescriptor } from 'react-intl';

import { ThinkingIndicator } from '../thinking-indicator/thinking-indicator';
import { PackingInlineBanner } from '../packing-inline-banner/packing-inline-banner';
import type { PackingInlineBannerStatus } from '../packing-inline-banner/packing-inline-banner';
import { PackingPackageList } from '../packing-package-list/packing-package-list';
import { formatPackageDimensions } from '../../utils/format-package-dimensions';
import { CONDENSED_BUTTON_FONT_FIX } from '../../panel-button-style';
import { messages } from '../../messages';
import cardStyle from '../../packing-card.module.css';
import style from './packing-result-card.module.css';
import type { BoxResult } from '../../types';

const MAX_VISIBLE_BOXES = 3;

type ErrorPresentation = {
  status: PackingInlineBannerStatus;
  bannerDescription: MessageDescriptor;
  bannerTitle?: MessageDescriptor;
  action: 'rerun' | 'retry';
  isActionDisabled?: boolean;
  shouldShowManageSettings?: boolean;
};

const ERROR_PRESENTATION: Record<string, ErrorPresentation> = {
  [PackingErrorCode.TIMEOUT]: {
    status: 'warning',
    bannerDescription: messages.calculationTimedOut,
    action: 'rerun',
  },
  [PackingErrorCode.NETWORK_ERROR]: {
    status: 'warning',
    bannerTitle: messages.errorConnectionTitle,
    bannerDescription: messages.calculationNetworkError,
    action: 'retry',
  },
  [PackingErrorCode.RATE_LIMITED]: {
    status: 'warning',
    bannerDescription: messages.calculationRateLimited,
    action: 'retry',
  },
  [PackingErrorCode.SERVICE_UNAVAILABLE]: {
    status: 'error',
    bannerDescription: messages.calculationServiceUnavailable,
    action: 'retry',
  },
  [PackingErrorCode.NO_SOLUTION_FOUND]: {
    status: 'error',
    bannerTitle: messages.errorNoSolutionTitle,
    bannerDescription: messages.calculationNoSolutionFound,
    action: 'retry',
    isActionDisabled: true,
    shouldShowManageSettings: true,
  },
};

const DEFAULT_ERROR_PRESENTATION: ErrorPresentation = {
  status: 'error',
  bannerDescription: messages.calculationFailed,
  action: 'retry',
};

const BoxLabel = ({ name, dims }: { name: ReactNode; dims: string | null }) => (
  <>
    <span className={style.packageName}>{name}</span>
    {dims && <span className={style.packageDims}>{` (${dims})`}</span>}
  </>
);

type PackingResultCardProps = {
  isCalculating: boolean;
  result: JobResultResponse | null;
  error: PackingError | null;
  sellerDimensionUnit: string;
  onCancel: () => void;
  onApply: () => void;
  onRerun?: () => void;
};

export const PackingResultCard = ({
  isCalculating,
  result,
  error,
  sellerDimensionUnit,
  onCancel,
  onApply,
  onRerun,
}: PackingResultCardProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [isShowingAll, setIsShowingAll] = useState(false);

  useEffect(() => {
    setIsShowingAll(false);
  }, [result]);

  const handleShowAll = useCallback(() => {
    setIsShowingAll(true);
  }, []);

  const handleManageSettings = useCallback(() => {
    navigate(getPackagesCustomSettingsPath());
  }, [navigate]);

  const optimalBox = result?.optimalBox;

  const allBoxes: BoxResult[] = useMemo(() => {
    if (result?.isMultiBox && result?.boxes && result.boxes.length > 0) {
      return result.boxes;
    }
    if (optimalBox) {
      return [{ box: optimalBox, itemPositions: result?.itemPositions }];
    }
    return emptyArray as BoxResult[];
  }, [result, optimalBox]);

  const formatDimensions = useCallback(
    (box: BoxResult['box']): string | null => formatPackageDimensions(box, sellerDimensionUnit, intl),
    [sellerDimensionUnit, intl],
  );

  if (isCalculating) {
    return (
      <FlexRow className={classnames(cardStyle.card, cardStyle.cardAnimated, cardStyle.cardMargin)} gap={Gap.Space8}>
        <div className={cardStyle.cardIconWrapper}>
          <ThinkingIndicator />
        </div>
        <FlexColumn className={cardStyle.cardBody}>
          <FlexRow className={cardStyle.cardHeadline}>
            <Text className={cardStyle.cardTitle} textType={TextType.Label} value={messages.makingRecommendation} />
            <MantineButton aria-label={intl.formatMessage(messages.cancel)} onClick={onCancel} size='condensed' variant='ghost'>
              <FontAwesomeIcon className={style.closeIcon} icon={faTimes} />
            </MantineButton>
          </FlexRow>
        </FlexColumn>
      </FlexRow>
    );
  }

  if (error) {
    const presentation = ERROR_PRESENTATION[error.code] ?? DEFAULT_ERROR_PRESENTATION;
    const isKnownCode = Boolean(ERROR_PRESENTATION[error.code]);
    const bannerDescription = isKnownCode || !error.message ? <FormattedMessage {...presentation.bannerDescription} /> : error.message;

    return (
      <FlexRow className={classnames(cardStyle.card, cardStyle.cardAnimated, cardStyle.cardMargin)} gap={Gap.Space8}>
        <div className={cardStyle.cardIconWrapper}>
          <FontAwesomeIcon className={cardStyle.cardIcon} icon={faSparklesSolid} />
        </div>
        <FlexColumn className={cardStyle.cardBody}>
          <FlexRow className={cardStyle.cardHeadline}>
            <Text className={cardStyle.cardTitle} textType={TextType.Label} value={messages.panelTitle} />
            <FlexRow className={cardStyle.cardHeadlineActions} gap={Gap.Space8}>
              {onRerun && (
                <MantineButton
                  disabled={presentation.isActionDisabled}
                  leftSection={presentation.action === 'rerun' ? <FontAwesomeIcon icon={faArrowRotateRight} /> : undefined}
                  onClick={onRerun}
                  size='condensed'
                  style={CONDENSED_BUTTON_FONT_FIX}
                  variant='tertiary'
                >
                  <FormattedMessage {...(presentation.action === 'rerun' ? messages.rerun : messages.retry)} />
                </MantineButton>
              )}
              <MantineButton aria-label={intl.formatMessage(messages.cancel)} onClick={onCancel} size='condensed' variant='ghost'>
                <FontAwesomeIcon className={style.closeIcon} icon={faTimes} />
              </MantineButton>
            </FlexRow>
          </FlexRow>
          <PackingInlineBanner
            status={presentation.status}
            title={presentation.bannerTitle ? <FormattedMessage {...presentation.bannerTitle} /> : undefined}
            description={bannerDescription}
            actions={
              presentation.shouldShowManageSettings ? (
                <MantineButton onClick={handleManageSettings} size='condensed' style={CONDENSED_BUTTON_FONT_FIX} variant='tertiary'>
                  <FormattedMessage {...messages.manageSettings} />
                </MantineButton>
              ) : undefined
            }
          />
        </FlexColumn>
      </FlexRow>
    );
  }

  if (!result) return null;

  const isMultiBox = result.isMultiBox ?? false;

  const visibleBoxes = isShowingAll ? allBoxes : allBoxes.slice(0, MAX_VISIBLE_BOXES);
  const remainingCount = allBoxes.length - visibleBoxes.length;

  return (
    <FlexRow className={classnames(cardStyle.card, cardStyle.cardAnimated, cardStyle.cardMargin)} gap={Gap.Space8}>
      <div className={cardStyle.cardIconWrapper}>
        <FontAwesomeIcon className={cardStyle.cardIcon} icon={faSparklesSolid} />
      </div>
      <FlexColumn className={cardStyle.cardBody} gap={Gap.Space8}>
        <FlexRow className={cardStyle.cardHeadline}>
          <Text className={cardStyle.cardTitle} textType={TextType.Label} value={messages.panelTitle} />
          {onRerun && (
            <MantineButton onClick={onRerun} size='condensed' style={CONDENSED_BUTTON_FONT_FIX} variant='tertiary'>
              <FormattedMessage {...messages.rerun} />
            </MantineButton>
          )}
        </FlexRow>
        {isMultiBox ? (
          <PackingPackageList boxes={visibleBoxes} sellerDimensionUnit={sellerDimensionUnit} />
        ) : (
          <Text
            textType={TextType.Paragraph}
            value={
              <FormattedMessage
                {...messages.allItemsGoInto}
                values={{
                  packageName: optimalBox?.name ?? '',
                  bold: (chunks: ReactNode) => <BoxLabel name={chunks} dims={formatDimensions(optimalBox)} />,
                }}
              />
            }
          />
        )}
        {!isShowingAll && remainingCount > 0 && (
          <div>
            <Button
              ariaLabel={intl.formatMessage(messages.andMorePackages, { count: remainingCount })}
              className={style.morePackagesButton}
              onClick={handleShowAll}
              theme={THEME_CONSTANTS.unstyled}
            >
              <FormattedMessage {...messages.andMorePackages} values={{ count: remainingCount }} />
            </Button>
          </div>
        )}
        <FlexRow gap={Gap.Space8}>
          <MantineButton onClick={onApply} size='condensed' style={CONDENSED_BUTTON_FONT_FIX} variant='tertiary'>
            <FormattedMessage {...messages.apply} />
          </MantineButton>
          <MantineButton onClick={onCancel} size='condensed' style={CONDENSED_BUTTON_FONT_FIX} variant='ghost'>
            <FormattedMessage {...messages.reject} />
          </MantineButton>
        </FlexRow>
      </FlexColumn>
    </FlexRow>
  );
};
